<template>
    <section class="flex flex-col gap-5">
        <BaseButton theme="tertiary" size="sm" class="self-start" @click="emit('back')"> <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("common.back") }} </BaseButton>

        <header class="flex flex-col gap-2">
            <div class="flex items-center gap-3 flex-wrap">
                <ReflectionIcon :kind="isSelfMode ? 'self' : 'sixty'" size="md" />
                <h2 class="title-subsection text-ink">{{ headerTitle }}</h2>
                <BaseChip v-if="!isSelfMode" size="2xs">{{ t("reflections.hub.step", { current: 1, total: 3 }) }}</BaseChip>
            </div>
            <p class="scaffold-body-small text-ink-muted">{{ subtitle }}</p>
        </header>

        <HorizontalDivider as="hr" spacing="none" />

        <div v-if="loading" class="scaffold-body-small text-ink-muted">{{ t("reflectionsAdmin.panel.loading") }}</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />
        <!-- The seeded template is global, so an empty list means active campaigns own every template, not that none are enabled. -->
        <BaseCard v-else-if="!templates.length" padding="md">
            <p class="body-small text-ink-muted">{{ t("reflections.start.startedFromUpcoming") }}</p>
        </BaseCard>
        <template v-else>
            <BaseCard v-if="templates.length > 1" padding="md">
                <div class="flex flex-col gap-2">
                    <span class="body-semibold text-ink">{{ t("reflections.sixty.start.template") }}</span>
                    <BaseSelect
                        v-model="selectedTemplateId"
                        :aria-label="t('reflections.sixty.start.templateOptions')"
                        :options="templateOptions"
                        trigger-class="border-border-strong! rounded-lgr! py-2! px-3! text-ink! scaffold-body-small"
                        content-class="border-border-strong! rounded-xl!" />
                    <span v-if="!isSelfMode && unlockNote" class="scaffold-details-small text-ink-muted">{{ unlockNote }}</span>
                </div>
            </BaseCard>

            <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />

            <!-- Always rendered so a 409 recovery (the form swapped for the notice) is announced to assistive tech. -->
            <div class="sr-only" role="status" aria-live="polite">{{ recoveryAnnouncement }}</div>
            <BaseCard v-if="existingCycle" theme="cream-muted" padding="md" data-testid="existing-cycle-notice">
                <div ref="existingNoticeEl" tabindex="-1" class="flex flex-col gap-3 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-control-border-focus">
                    <div class="flex flex-col gap-1">
                        <div class="body-semibold text-ink">{{ t("reflections.sixty.start.existing.title", { name: existingCycle.template_name }) }}</div>
                        <div class="scaffold-body-small text-ink-muted">{{ existingCycleSummary }}</div>
                    </div>
                    <div class="flex flex-wrap gap-2">
                        <BaseButton theme="primary" :disabled="deleting" @click="onContinueExisting">{{ continueLabel }}</BaseButton>
                        <BaseButton v-if="existingCycle.can_delete !== false" theme="tertiary" :disabled="deleting" @click="showDeleteConfirm = true">
                            {{ t("reflections.sixty.start.existing.deleteFresh") }}
                        </BaseButton>
                    </div>
                </div>
            </BaseCard>
            <template v-else>
                <BaseCard v-if="!isSelfMode" padding="md">
                    <label for="self-assess" class="flex items-start gap-3 cursor-pointer">
                        <input id="self-assess" v-model="selfAssess" type="checkbox" class="mt-1 accent-control-accent" />
                        <span class="flex flex-col gap-1">
                            <span class="body-semibold text-ink">{{ t("reflections.sixty.start.selfAssess.title") }}</span>
                            <span class="scaffold-body-small text-ink-muted">
                                {{ t("reflections.sixty.start.selfAssess.body") }}
                            </span>
                        </span>
                    </label>
                </BaseCard>

                <BaseCard v-if="!isSelfMode" padding="md">
                    <label for="personal-note" class="flex flex-col gap-2">
                        <span class="body-semibold text-ink"
                            >{{ t("reflections.sixty.start.personalNote.label") }}
                            <span class="body text-ink-muted">{{ t("reflections.sixty.start.personalNote.optional") }}</span></span
                        >
                        <textarea
                            id="personal-note"
                            v-model="personalNote"
                            rows="3"
                            :placeholder="t('reflections.sixty.start.personalNote.placeholder')"
                            class="w-full px-3 py-2 border border-control-border rounded-lgr resize-none scaffold-body-small focus-field"></textarea>
                    </label>
                </BaseCard>

                <BaseCard v-if="!isSelfMode" theme="cream-muted" padding="md" class="flex items-start gap-3">
                    <i class="bi bi-shield-check text-notification-success-text mt-1" aria-hidden="true"></i>
                    <div class="flex flex-col gap-1">
                        <div class="body-semibold text-ink">{{ t("reflections.sixty.start.anonymous.title") }}</div>
                        <div class="scaffold-body-small text-ink-muted">
                            {{ t("reflections.sixty.start.anonymous.body") }}
                        </div>
                    </div>
                </BaseCard>

                <div class="flex flex-wrap items-center justify-end gap-3">
                    <!-- Self-launched reflections auto-expire at day 14; say so where the clock starts. -->
                    <span v-if="isSelfMode" class="scaffold-details-small text-ink-muted">{{ t("reflections.sixty.start.selfExpiryNote") }}</span>
                    <BaseButton theme="primary" :disabled="creating" @click="onCreate">
                        {{ creating ? t("reflections.sixty.start.creating") : isSelfMode ? t("reflections.sixty.start.startSelf") : t("reflections.sixty.start.next") }}
                    </BaseButton>
                </div>
            </template>
        </template>
    </section>
    <ConfirmationDialog
        v-model:open="showDeleteConfirm"
        :title="deleteTitle"
        :text="deleteText"
        :confirm-label="t('common.delete')"
        confirm-theme="danger"
        :confirm-disabled="deleting"
        @confirm="onConfirmDelete" />
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { CycleEnvelopeResponse, ExistingCycleSummary, StartCycleContextResponse, TemplateSummary } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import ReflectionIcon from "~vue/pages/reflections/ReflectionIcon.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { formatDate } from "~vue/utils/formatDate";
import { formatNumber } from "~vue/utils/formatNumber";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

interface Props {
    // "self" opens the self-only flow (no rater fields, cycle auto-launches on create).
    // "360" is the full 360 setup (template + self-assess toggle + personal note).
    mode?: "self" | "360";
}

const { mode = "360" } = defineProps<Props>();

const emit = defineEmits<{
    (event: "back"): void;
    // Fired after the backend creates the cycle. Payload lets the parent
    // decide the next inline view — self-only cycles jump into the take flow,
    // 360 cycles jump into Nominate.
    (event: "created", payload: { cycleId: string; isSelfAssessmentOnly: boolean; selfAssessEnabled: boolean; title: string; closesAt: string | null }): void;
    // Fired when the user picks up the cycle they already have on the selected
    // template instead of starting another. Same hand-off as "created", but
    // nothing new exists, so the parent needn't replace history.
    (event: "continue", payload: { cycleId: string; isSelfAssessmentOnly: boolean; title: string; closesAt: string | null }): void;
    // Fired after "Delete and start fresh" succeeds, so the hub drops the
    // cycle from its Ready-to-start rows too.
    (event: "deleted", cycleId: string): void;
}>();

const { t } = useI18n();

const isSelfMode = computed(() => mode === "self");

const templates = ref<TemplateSummary[]>([]);
const selectedTemplateId = ref<string | null>(null);
const templateOptions = computed(() => templates.value.map((templateOption) => ({ key: templateOption.id, label: templateOption.name })));
const defaultMinResponses = ref<number | null>(null);
const selectedTemplate = computed(() => templates.value.find((templateOption) => templateOption.id === selectedTemplateId.value) ?? null);
const minResponses = computed<number | null>(() => selectedTemplate.value?.min_responses ?? defaultMinResponses.value);
const questionCount = computed<number | null>(() => {
    const selected = templates.value.find((templateOption) => templateOption.id === selectedTemplateId.value);
    return selected?.question_count ?? null;
});
const questionCountLabel = computed<string>(() => {
    const n = questionCount.value;
    if (n === null) {
        return "";
    }
    return t("reflections.sixty.start.questionCount", { count: n }, n);
});

const loading = ref(true);
const loadError = ref<string | null>(null);
const selfAssess = ref(true);
// Category thresholds replace the overall minimum, so neither the subtitle nor
// the picker note can promise an unlock here. The nomination step spells the
// thresholds out instead.
const hasCategoryThresholds = computed(() => (selectedTemplate.value?.category_thresholds?.thresholds?.length ?? 0) > 0);
const unlockNote = computed(() => (hasCategoryThresholds.value ? null : t("reflections.sixty.start.unlockNote", { min: formatNumber(minResponses.value) })));

const personalNote = ref("");
const creating = ref(false);
const error = ref<string | null>(null);

// One open cycle per template and mode: the API rejects a second with a 409,
// so the form gives way to the existing one for the selected template.
const existingCycles = ref<ExistingCycleSummary[]>([]);
const existingCycle = computed(() => existingCycles.value.find((cycle) => cycle.template_id === selectedTemplateId.value) ?? null);
const existingNoticeEl = ref<HTMLElement | null>(null);
const recoveryAnnouncement = ref("");

// After a 409 the Next button the user just pressed is gone; say so and put
// focus on the notice that replaced it, so Continue is the next Tab stop.
async function focusRecoveredNotice() {
    const cycle = existingCycle.value;
    if (!cycle) {
        return;
    }
    recoveryAnnouncement.value = t("reflections.sixty.start.existing.announcement", { name: cycle.template_name, action: continueLabel.value });
    await nextTick();
    existingNoticeEl.value?.focus();
}
const showDeleteConfirm = ref(false);
const deleting = ref(false);

function startedLabel(cycle: ExistingCycleSummary): string {
    return formatDate(cycle.created_at, "month-day");
}

const existingCycleSummary = computed(() => {
    const cycle = existingCycle.value;
    if (!cycle) {
        return "";
    }
    if (cycle.is_self_assessment_only) {
        return (cycle.answered_count ?? 0) > 0
            ? t("reflections.sixty.start.existing.selfProgress", { answered: formatNumber(cycle.answered_count ?? 0), total: formatNumber(cycle.question_count ?? 0) })
            : t("reflections.sixty.start.existing.selfStarted", { date: startedLabel(cycle) });
    }
    if (cycle.state === "draft") {
        return t("reflections.sixty.start.existing.draft", { date: startedLabel(cycle), count: cycle.invited_count });
    }
    const progress = t("reflections.sixty.start.existing.collecting", { invited: formatNumber(cycle.invited_count), responded: formatNumber(cycle.responded_count) });
    return cycle.can_delete === false ? t("reflections.sixty.start.existing.undeletable", { progress }) : progress;
});

const continueLabel = computed(() => {
    const cycle = existingCycle.value;
    if (!cycle) {
        return "";
    }
    if (cycle.is_self_assessment_only) {
        return (cycle.answered_count ?? 0) > 0 ? t("reflections.sixty.start.existing.resume") : t("reflections.sixty.start.existing.continue");
    }
    return cycle.state === "draft" ? t("reflections.sixty.start.existing.continueSetup") : t("reflections.sixty.start.existing.addRaters");
});

const deleteTitle = computed(() => (existingCycle.value?.is_self_assessment_only ? t("reflections.shared.deleteCycle.titleSelf") : t("reflections.shared.deleteCycle.title360")));
const deleteText = computed(() => {
    const cycle = existingCycle.value;
    if (!cycle) {
        return "";
    }
    if (cycle.is_self_assessment_only) {
        return t("reflections.shared.deleteCycle.bodySelf");
    }
    if (cycle.state === "draft") {
        return t("reflections.shared.deleteCycle.bodyDraft");
    }
    return t("reflections.shared.deleteCycle.bodyCollecting");
});

function onContinueExisting() {
    const cycle = existingCycle.value;
    if (!cycle) {
        return;
    }
    emit("continue", {
        cycleId: cycle.id,
        isSelfAssessmentOnly: cycle.is_self_assessment_only ?? false,
        title: cycle.template_name,
        closesAt: cycle.closes_at ?? null,
    });
}

async function onConfirmDelete() {
    const cycle = existingCycle.value;
    if (!cycle || deleting.value) {
        return;
    }
    deleting.value = true;
    error.value = null;
    // 204 with no body on success, so don't ask for JSON; the wrapper already
    // puts the API's error message on `error.value.message` for non-2xx.
    const { error: fetchError } = await useFetch(`/v1/reflections/cycles/${cycle.id}`).delete();
    deleting.value = false;
    showDeleteConfirm.value = false;
    if (fetchError.value) {
        error.value = fetchError.value.message || t("reflections.shared.deleteCycle.failed");
        return;
    }
    existingCycles.value = existingCycles.value.filter((candidate) => candidate.id !== cycle.id);
    emit("deleted", cycle.id);
}

const headerTitle = computed(() => (isSelfMode.value ? t("reflections.sixty.start.titleSelf") : t("reflections.sixty.start.title360")));

const subtitle = computed(() => {
    if (isSelfMode.value) {
        return t("reflections.sixty.start.subtitle.self", { questions: questionCountLabel.value });
    }
    if (hasCategoryThresholds.value) {
        return t("reflections.sixty.start.subtitle.thresholds", { questions: questionCountLabel.value });
    }
    if (minResponses.value === null) {
        return t("reflections.sixty.start.subtitle.noMinimum");
    }
    if (minResponses.value === 0) {
        return t("reflections.sixty.start.subtitle.immediate", { questions: questionCountLabel.value });
    }
    return t("reflections.sixty.start.subtitle.minimum", { questions: questionCountLabel.value, min: formatNumber(minResponses.value) });
});

async function loadContext() {
    const startContextUrl = isSelfMode.value ? "/v1/reflections/start-context?mode=self" : "/v1/reflections/start-context";
    const { data, error: fetchError } = await useFetch(startContextUrl).json();
    loading.value = false;
    if (fetchError.value) {
        loadError.value = (data.value as { error?: string })?.error || (isSelfMode.value ? t("reflections.sixty.start.loadErrorSelf") : t("reflections.sixty.start.loadError360"));
        return;
    }
    const ctx = (data.value as StartCycleContextResponse).context;
    defaultMinResponses.value = ctx.min_responses;
    templates.value = ctx.templates;
    existingCycles.value = ctx.existing_cycles ?? [];
    if (!selectedTemplateId.value || !ctx.templates.some((templateOption) => templateOption.id === selectedTemplateId.value)) {
        selectedTemplateId.value = ctx.default_template_id ?? null;
    }
}

onMounted(async () => {
    await loadContext();
    if (loadError.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_START_VIEWED, {
        template_count: templates.value.length,
        has_default_template: selectedTemplateId.value !== null,
        mode: isSelfMode.value ? "self_assessment" : "360",
    });
});

async function onCreate() {
    if (creating.value) {
        return;
    }
    creating.value = true;
    error.value = null;
    try {
        const {
            data,
            error: fetchError,
            statusCode,
        } = await useFetch("/v1/reflections/cycles")
            .post(
                JSON.stringify({
                    template_id: selectedTemplateId.value,
                    is_self_assessment_only: isSelfMode.value,
                    self_assess_enabled: isSelfMode.value ? true : selfAssess.value,
                    personal_note: isSelfMode.value ? "" : personalNote.value,
                }),
                "application/json",
            )
            .json();

        if (fetchError.value) {
            // 409: a cycle on this template already exists (started in another
            // tab, or the context was stale). Re-read it so the notice above
            // offers to continue it rather than erroring.
            if (statusCode.value === 409) {
                await loadContext();
                if (existingCycle.value) {
                    await focusRecoveredNotice();
                    return;
                }
            }
            error.value = (data.value as { error?: string })?.error || t("reflections.sixty.start.createError");
            return;
        }

        const cycle = (data.value as CycleEnvelopeResponse).cycle;
        const templateName = templates.value.find((templateOption) => templateOption.id === selectedTemplateId.value)?.name ?? t("reflections.sixty.start.defaultTitle");
        emit("created", {
            cycleId: cycle.id,
            isSelfAssessmentOnly: isSelfMode.value,
            selfAssessEnabled: isSelfMode.value ? true : selfAssess.value,
            title: templateName,
            closesAt: cycle.closes_at ?? null,
        });
    } finally {
        creating.value = false;
    }
}
</script>
